import React, { useRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PHONE_MAX_DIGITS,
  PHONE_MIN_DIGITS,
  formatPhoneAsYouType,
  isPlausiblePhone,
  normalizeDialCode,
} from '@/lib/phone';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The plausibility range from lib/phone (7–15 digits), not a US ten-digit
 * floor: a Swiss mobile is nine digits (SCRUM-1833).
 */
export const MIN_PHONE_DIGITS = PHONE_MIN_DIGITS;
export const MAX_PHONE_DIGITS = PHONE_MAX_DIGITS;

/** Strip everything that isn't a digit */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Format as typed for the dial code: (555) 555-5555 for code '1' (digits past
 * ten trail as a space-separated suffix, so a pasted extension survives
 * round-tripping), libphonenumber's grouping for any other code.
 */
function formatPhone(digits: string, maxDigits: number, countryCode: string): string {
  return formatPhoneAsYouType(digits.slice(0, maxDigits), countryCode);
}

/**
 * Shared length rule for required phone fields: the 7–15 digit plausibility
 * check, capped at `maxDigits`. Returns an error message, or null.
 */
export function phoneLengthError(
  value: string,
  maxDigits: number = MAX_PHONE_DIGITS,
): string | null {
  const digits = digitsOnly(value);
  if (digits.length > maxDigits) {
    return `Phone number cannot be longer than ${maxDigits} digits`;
  }
  if (!isPlausiblePhone(digits)) {
    return `Enter a full phone number (at least ${MIN_PHONE_DIGITS} digits)`;
  }
  return null;
}

/**
 * Live digit-count badge for the label row of a required phone field — pass to
 * `FormField`'s `labelExtra`. Mirrors {@link phoneLengthError} so the inline hint
 * and the submit-time error can never disagree.
 */
export function PhoneLengthIndicator({
  value,
  maxDigits = MAX_PHONE_DIGITS,
}: {
  value: string;
  maxDigits?: number;
}) {
  const count = digitsOnly(value).length;
  const isValid = phoneLengthError(value, maxDigits) === null;

  const [tone, text] = isValid
    ? ['bg-green-100 text-green-800', 'Valid']
    : count === 0
      ? ['bg-slate-100 text-slate-600', `${MIN_PHONE_DIGITS}+ digits required`]
      : count < MIN_PHONE_DIGITS
        ? ['bg-amber-100 text-amber-800', `${count} of ${MIN_PHONE_DIGITS}+ digits`]
        : ['bg-amber-100 text-amber-800', `${count} digits, too long`];

  return (
    <span
      // Politely announced so screen-reader users get the same feedback as the badge.
      role="status"
      aria-live="polite"
      className={cn(
        'ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
        tone,
      )}
    >
      {isValid && <Check className="h-3 w-3" aria-hidden="true" />}
      {text}
    </span>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> {
  /** Callback receives the raw digit string (e.g. "5555551234") */
  onChange?: (digits: string) => void;
  error?: string;
  /** Hard cap on typed/pasted digits. Defaults to {@link MAX_PHONE_DIGITS}. */
  maxDigits?: number;
  /** The dial code the number is formatted for, e.g. '44'. Defaults to '1'. */
  countryCode?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ onChange, error, className, value, id, maxDigits = MAX_PHONE_DIGITS, countryCode = '1', ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) ?? internalRef;

    // Convert controlled value (digits) back to display format
    const displayValue =
      value !== undefined ? formatPhone(digitsOnly(String(value)), maxDigits, countryCode) : undefined;

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value;
      const digits = digitsOnly(raw).slice(0, maxDigits);
      const formatted = formatPhone(digits, maxDigits, countryCode);

      // Update the input display value
      e.target.value = formatted;

      // Keep cursor position sane after reformatting
      const cursorPos = formatted.length;
      requestAnimationFrame(() => {
        if (resolvedRef && 'current' in resolvedRef && resolvedRef.current) {
          resolvedRef.current.setSelectionRange(cursorPos, cursorPos);
        }
      });

      onChange?.(digits);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      // Allow: backspace, delete, tab, escape, enter, arrows
      const allowed = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End',
      ];
      if (allowed.includes(e.key)) return;
      if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
      // Block non-digit keys
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }

    const errorId = error && id ? `${id}-error` : undefined;

    return (
      <div className="relative">
        <input
          ref={resolvedRef}
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder={normalizeDialCode(countryCode) === '1' ? '(555) 555-5555' : ''}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-invalid={!!error}
          aria-describedby={errorId}
          className={cn(
            'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900',
            'placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
            error && 'border-red-400 focus:ring-red-500 focus:border-red-500',
            className,
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
